import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowUpRight, Play, Pause, Maximize2, Volume2, VolumeX, Check,
  Users, GraduationCap, UserCheck, BarChart3, Handshake, Zap, Menu, X,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import { useLenis } from "../hooks/useLenis";
import { useSeo } from "../hooks/useSeo";
import { ApmJoinModal } from "../components/apm/ApmJoinModal";

const ease = [0.16, 1, 0.3, 1];

const IMG = {
  hist1: "/apm/story-01-wiek-17-laptop.png",
  hist2: "/apm/story-02-pierwszy-mercedes.png",
  hist3: "/apm/story-03-bmw-m2.png",
  workspace: "/apm/section-laptop-analytics.png",
  step1: "/apm/process-01-poznaj-apm.png",
  step2: "/apm/process-02-zglos-sie.png",
  step3: "/apm/process-03-zacznij-dzialac.png",
};

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 40, filter: "blur(10px)" }}
    whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
    viewport={{ once: true, margin: "-70px" }}
    transition={{ duration: 0.9, delay, ease }}
  >
    {children}
  </motion.div>
);

const Overline = ({ children, className = "" }) => (
  <p className={`text-[11px] uppercase tracking-[0.28em] text-white/40 ${className}`}>{children}</p>
);

const NAV = [
  { label: "O grupie", href: "#o-grupie" },
  { label: "Moja historia", to: "/apm/moja-historia" },
  { label: "Co zyskujesz", href: "#co-zyskujesz" },
  { label: "Członkostwo", href: "#jak-dolaczyc" },
];

const MARQUEE = ["Wiedza", "Biznes", "Strategia", "Coaching", "Rozwój", "Kontakty"];

const MILESTONES = [
  { n: "01", img: IMG.hist1, age: "17 lat", title: "pierwsze kroki w biznesie" },
  { n: "02", img: IMG.hist2, age: "18 lat", title: "pierwszy Mercedes" },
  { n: "03", img: IMG.hist3, age: "23 lata", title: "BMW M2 i zakup mieszkania" },
];

const REASONS = [
  { icon: "/apm/icon-01-praktyczna-wiedza.png", title: "Praktyczna wiedza", text: "Konkrety, które od razu zastosujesz w swoim biznesie i życiu." },
  { icon: "/apm/icon-02-sprawdzone-strategie.png", title: "Sprawdzone strategie", text: "Metody, które działają w realnym świecie, nie tylko w teorii." },
  { icon: "/apm/icon-03-coaching-wsparcie.png", title: "Coaching i wsparcie", text: "Pytasz, działasz, otrzymujesz feedback od bardziej doświadczonych." },
  { icon: "/apm/icon-04-wartosciowe-kontakty.png", title: "Wartościowe kontakty", text: "Poznajesz ludzi, z którymi współpracujesz i tworzysz możliwości." },
];

const CHECKLIST = [
  "analiza pomysłów i możliwości",
  "plan działania dopasowany do Ciebie",
  "unikanie kosztownych błędów",
  "rozwój krok po kroku",
];

const BENEFITS = [
  { icon: Users, title: "Zamknięta społeczność", text: "Selekcja członków, kultura działania i wzajemny szacunek." },
  { icon: GraduationCap, title: "Materiały i szkolenia", text: "Praktyczne kursy, nagrania, szablony i narzędzia gotowe do użycia." },
  { icon: UserCheck, title: "Regularny coaching", text: "Sesje Q&A, call'e, analiza case'ów i indywidualne wskazówki." },
  { icon: BarChart3, title: "Analiza działań", text: "Feedback do Twoich działań i pomoc w optymalizacji." },
  { icon: Handshake, title: "Kontakty i współprace", text: "Dostęp do wartościowych kontaktów i możliwości współpracy." },
  { icon: Zap, title: "Motywacja i odpowiedzialność", text: "Środowisko ambitnych ludzi, którzy wymagają więcej." },
];

const STEPS = [
  { n: "01", img: IMG.step1, title: "Poznaj APM", text: "Dowiedz się, jak działamy, i sprawdź, czy to miejsce dla Ciebie." },
  { n: "02", img: IMG.step2, title: "Wykup członkostwo", text: "Aktywuj dostęp za 149 zł/mies. i wejdź do zamkniętej społeczności APM." },
  { n: "03", img: IMG.step3, title: "Zacznij działać", text: "Dołącz do społeczności i zacznij realizować swoje cele." },
];

const FAQ = [
  { q: "Dla kogo jest APM?", a: "Dla ambitnych osób, które chcą się rozwijać w biznesie i życiu w otoczeniu ludzi grających o wysoką stawkę — niezależnie od punktu startu." },
  { q: "Jak wygląda proces dołączenia?", a: "Wysyłasz zgłoszenie, odpowiadasz na kilka pytań, a my sprawdzamy dopasowanie. Jeśli jest zgodne — otrzymujesz dostęp do społeczności." },
  { q: "Ile czasu potrzebuję, aby zobaczyć efekty?", a: "To zależy od Ciebie. Dostajesz wiedzę i wsparcie — tempo wyników zależy od Twojego zaangażowania i konsekwencji." },
  { q: "Czy potrzebuję doświadczenia?", a: "Nie. Liczy się nastawienie i gotowość do działania. Prowadzimy Cię krok po kroku, niezależnie od poziomu startowego." },
  { q: "Czy APM to gwarancja zarobków?", a: "Nie. APM przekazuje wiedzę i wsparcie. Wyniki zależą od Twoich decyzji, pracy i zaangażowania." },
];

const VideoBar = ({ videoRef }) => {
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onTime = () => setProgress(v.duration ? (v.currentTime / v.duration) * 100 : 0);
    v.addEventListener("timeupdate", onTime);
    return () => v.removeEventListener("timeupdate", onTime);
  }, [videoRef]);

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); } else { v.pause(); setPlaying(false); }
  };
  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };
  const fs = () => videoRef.current?.requestFullscreen?.();

  return (
    <div className="absolute bottom-6 left-6 right-6 z-20 flex items-center gap-4 rounded-full border border-white/10 bg-black/40 backdrop-blur-2xl px-5 py-3" data-testid="apm-video-bar">
      <button onClick={toggle} className="text-white/90 hover:text-[#a78bfa] transition-colors" data-testid="apm-video-play">
        {playing ? <Pause size={16} /> : <Play size={16} />}
      </button>
      <div className="flex-1 h-[3px] rounded-full bg-white/12 overflow-hidden">
        <div className="h-full bg-[#a78bfa]" style={{ width: `${progress}%` }} />
      </div>
      <button onClick={toggleMute} className="text-white/90 hover:text-[#a78bfa] transition-colors">
        {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
      </button>
      <button onClick={fs} className="text-white/90 hover:text-[#a78bfa] transition-colors">
        <Maximize2 size={14} />
      </button>
    </div>
  );
};

const AmbientGlow = ({ className = "" }) => (
  <div className={`pointer-events-none absolute rounded-full apm-blob ${className}`} style={{ background: "radial-gradient(circle, rgba(139,92,246,0.9), transparent 62%)" }} />
);

export default function Apm() {
  useLenis();
  useSeo({
    title: "APM — Elitarna społeczność biznesowa | Dołącz",
    description: "APM to elitarna społeczność ludzi grających o wysoką stawkę. Praktyczna wiedza, sprawdzone strategie, coaching i wartościowe kontakty. Dołącz do APM.",
    canonical: "https://pmpoint.pl/apm",
  });
  const [modal, setModal] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const videoRef = useRef(null);

  const heroRef = useRef(null);
  const { scrollYProgress: heroP } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(heroP, [0, 1], [0, 120]);
  const heroScale = useTransform(heroP, [0, 1], [1, 1.12]);

  return (
    <div className="relative min-h-screen text-white overflow-x-hidden" style={{ background: "#030206" }} data-testid="apm-page">
      <div className="apm-noise" aria-hidden="true" />
      <div className="apm-aurora" aria-hidden="true">
        <span className="a1" />
        <span className="a2" />
        <span className="a3" />
      </div>

      {/* NAV */}
      <header className="fixed top-0 inset-x-0 z-50 border-b border-white/[0.06] bg-[#030206]/60 backdrop-blur-2xl">
        <nav className="container-pm flex items-center justify-between h-[74px]">
          <a href="#top" className="font-display font-medium text-xl tracking-[0.32em] text-white" data-testid="apm-logo">APM</a>
          <div className="hidden lg:flex items-center gap-10">
            {NAV.map((n) => (
              n.to ? (
                <Link key={n.label} to={n.to} className="text-[11px] uppercase tracking-[0.18em] text-white/50 hover:text-white transition-colors">
                  {n.label}
                </Link>
              ) : (
                <a key={n.label} href={n.href} className="text-[11px] uppercase tracking-[0.18em] text-white/50 hover:text-white transition-colors">
                  {n.label}
                </a>
              )
            ))}
          </div>
          <button
            onClick={() => setModal(true)}
            data-testid="apm-nav-join"
            className="group hidden lg:inline-flex items-center gap-2 h-10 px-5 rounded-full border border-white/15 bg-white/[0.03] backdrop-blur text-white text-[11px] uppercase tracking-[0.16em] hover:bg-white/[0.08] transition-all"
          >
            Dołącz <ArrowUpRight size={14} className="text-[#a78bfa] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </button>
          <button
            onClick={() => setMenuOpen(true)}
            data-testid="apm-menu-open"
            aria-label="Otwórz menu"
            className="lg:hidden flex items-center justify-center w-10 h-10 rounded-full border border-white/15 bg-white/[0.03] text-white"
          >
            <Menu size={18} />
          </button>
        </nav>
      </header>

      {/* MOBILE MENU */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] lg:hidden"
            data-testid="apm-mobile-menu"
          >
            <div className="absolute inset-0 bg-[#030206]/95 backdrop-blur-2xl" onClick={() => setMenuOpen(false)} />
            <div className="relative h-full flex flex-col">
              <div className="container-pm flex items-center justify-between h-[74px] border-b border-white/[0.06]">
                <span className="font-display font-medium text-xl tracking-[0.32em] text-white">APM</span>
                <button
                  onClick={() => setMenuOpen(false)}
                  data-testid="apm-menu-close"
                  aria-label="Zamknij menu"
                  className="flex items-center justify-center w-10 h-10 rounded-full border border-white/15 bg-white/[0.03] text-white"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="container-pm flex-1 flex flex-col justify-center gap-2">
                {NAV.map((n, i) => (
                  n.to ? (
                    <Link
                      key={n.label}
                      to={n.to}
                      onClick={() => setMenuOpen(false)}
                      className="font-display font-light text-white text-3xl py-3 border-b border-white/[0.06] tracking-tight"
                    >
                      {n.label}
                    </Link>
                  ) : (
                    <motion.a
                      key={n.label}
                      href={n.href}
                      onClick={() => setMenuOpen(false)}
                      initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.08 + i * 0.06, duration: 0.4 }}
                      className="font-display font-light text-white text-3xl py-3 border-b border-white/[0.06] tracking-tight"
                    >
                      {n.label}
                    </motion.a>
                  )
                ))}
                <button
                  onClick={() => { setMenuOpen(false); setModal(true); }}
                  data-testid="apm-menu-join"
                  className="btn-premium mt-8 inline-flex items-center justify-center gap-2 h-[56px] px-8 rounded-full bg-[#8b5cf6] text-white text-[13px] uppercase tracking-[0.12em] font-semibold"
                >
                  Dołącz do APM <ArrowRight size={17} />
                </button>
                <p className="mt-4 text-sm text-white/45">Członkostwo już od <span className="text-[#c4b3fb] font-medium">149 zł/mies.</span></p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* HERO */}
      <section ref={heroRef} id="top" className="relative min-h-[100svh] w-full overflow-hidden">
        <motion.div className="absolute inset-0" style={{ y: heroY, scale: heroScale }}>
          <video
            ref={videoRef}
            className="absolute inset-0 w-full h-full object-cover"
            src="/apm/hero.mp4"
            autoPlay muted loop playsInline
            data-testid="apm-hero-video"
          />
        </motion.div>
        <div className="absolute inset-0 bg-black/45" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg,#030206 0%,rgba(3,2,6,0.85) 34%,rgba(3,2,6,0.35) 66%,rgba(3,2,6,0.1) 100%)" }} />
        <div className="absolute inset-x-0 bottom-0 h-[45%]" style={{ background: "linear-gradient(0deg,#030206 6%,transparent 100%)" }} />

        <div className="relative z-10 container-pm min-h-[100svh] flex flex-col justify-center pt-24 pb-32">
          <motion.div
            initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2, duration: 0.8 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="h-px w-12 bg-gradient-to-r from-[#8b5cf6] to-transparent" />
            <Overline className="text-[#a78bfa]/80">Elitarna społeczność</Overline>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 30, filter: "blur(12px)" }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }} transition={{ delay: 0.3, duration: 1, ease }}
            className="font-display font-light leading-[1.02] tracking-[-0.03em] text-[clamp(40px,6vw,86px)] max-w-[17ch]"
          >
            Chcesz poznać zasady, których{" "}
            <span className="font-serif-apm italic font-medium text-[#c4b3fb]">nie uczą w szkole?</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.8 }}
            className="mt-8 max-w-[50ch] text-base sm:text-lg text-white/60 leading-relaxed"
          >
            Dołącz do grupy APM za jedyne <span className="text-[#c4b3fb] font-medium">149 zł/mies.</span> Zdobądź praktyczną wiedzę, poznaj sprawdzone strategie i naucz się budować większe zarobki krok po kroku.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.8 }}
            className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
          >
            <button
              onClick={() => setModal(true)}
              data-testid="apm-hero-join"
              className="btn-premium group inline-flex items-center gap-2.5 h-[56px] px-8 rounded-full bg-[#8b5cf6] text-white text-[13px] uppercase tracking-[0.12em] font-semibold"
            >
              Dołącz do APM <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <Link
              to="/apm/moja-historia"
              className="inline-flex items-center gap-2 h-[56px] px-8 rounded-full border border-white/15 bg-white/[0.02] backdrop-blur text-white text-[13px] uppercase tracking-[0.12em] font-medium hover:bg-white/[0.06] transition-colors"
              data-testid="apm-hero-historia"
            >
              Poznaj moją historię
            </Link>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.95, duration: 0.8 }}
            className="mt-5 text-sm text-white/45"
          >
            Członkostwo już od <span className="text-[#c4b3fb] font-medium">149 zł/mies.</span> · dołączasz w kilka minut
          </motion.p>
        </div>
        <VideoBar videoRef={videoRef} />
      </section>

      {/* EDITORIAL RIBBON */}
      <div className="relative border-y border-white/[0.06] py-8 overflow-hidden" style={{ background: "#030206" }} data-testid="apm-marquee">
        <div className="pm-marquee pm-marquee-vslow">
          {[...MARQUEE, ...MARQUEE, ...MARQUEE, ...MARQUEE].map((w, i) => (
            <span key={i} className="flex items-center whitespace-nowrap">
              <span className="font-serif-apm italic text-base sm:text-lg text-white/45 uppercase tracking-[0.3em] px-10">{w}</span>
              <span className="text-[#8b5cf6]/70 text-sm">✦</span>
            </span>
          ))}
        </div>
      </div>

      {/* MOJA HISTORIA */}
      <section id="moja-historia" className="relative py-[clamp(80px,11vw,150px)]">
        <div className="container-pm">
          <Reveal className="max-w-[760px] mb-16">
            <Overline className="mb-6">Moja historia</Overline>
            <h2 className="font-display font-light text-[clamp(30px,4.6vw,62px)] leading-[1.04] tracking-[-0.025em]">
              Zacząłem <span className="font-serif-apm italic font-medium text-[#c4b3fb]">wcześniej</span> niż większość.
            </h2>
            <p className="mt-6 text-white/55 text-base sm:text-lg leading-relaxed max-w-[54ch]">
              Nie pokazuję tego, żeby się chwalić. Pokazuję, że właściwa wiedza, konsekwencja i działanie zmieniają wynik.
            </p>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {MILESTONES.map((m, i) => (
              <Reveal key={m.n} delay={i * 0.14}>
                <div className="group relative rounded-3xl overflow-hidden border border-white/10" data-testid={`apm-milestone-${m.n}`}>
                  <span className="pointer-events-none absolute -top-6 left-3 z-10 font-display font-light text-[9rem] leading-none text-white/[0.06] select-none">{m.n}</span>
                  <div className="relative h-[360px] overflow-hidden">
                    <img src={m.img} alt={m.title} className="w-full h-full object-cover grayscale group-hover:grayscale-0 scale-105 group-hover:scale-100 transition-all duration-[900ms] ease-out" />
                    <div className="absolute inset-0" style={{ background: "linear-gradient(180deg,rgba(3,2,6,0.1) 0%,rgba(3,2,6,0.55) 55%,rgba(3,2,6,0.96) 100%)" }} />
                  </div>
                  <div className="absolute bottom-0 inset-x-0 p-7 flex items-end justify-between">
                    <div>
                      <p className="font-display font-light text-3xl text-white tracking-tight">{m.age}</p>
                      <p className="text-sm text-white/55 mt-1.5">{m.title}</p>
                    </div>
                    <span className="flex items-center justify-center w-10 h-10 rounded-full border border-white/15 bg-white/[0.04] backdrop-blur text-[#a78bfa] translate-y-1 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                      <ArrowUpRight size={16} />
                    </span>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-12 flex justify-center">
            <Link
              to="/apm/moja-historia"
              data-testid="apm-read-story"
              className="group inline-flex items-center gap-2.5 h-[52px] px-8 rounded-full border border-white/15 bg-white/[0.03] backdrop-blur text-white text-[13px] uppercase tracking-[0.12em] font-medium hover:bg-white/[0.07] transition-all"
            >
              Przeczytaj całą historię
              <ArrowUpRight size={16} className="text-[#a78bfa] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* NIE MUSISZ SAM */}
      <section id="o-grupie" className="relative py-[clamp(64px,8vw,120px)] border-y border-white/[0.06] overflow-hidden" style={{ background: "#0A0812" }}>
        <AmbientGlow className="-top-40 left-1/4 w-[520px] h-[520px] opacity-30" />
        <div className="container-pm relative">
          <Reveal className="max-w-[720px] mb-16">
            <Overline className="mb-6">Dlaczego APM</Overline>
            <h2 className="font-display font-light text-[clamp(28px,4.4vw,56px)] leading-[1.04] tracking-[-0.025em]">
              Nie musisz dochodzić do wszystkiego <span className="font-serif-apm italic font-medium text-[#c4b3fb]">sam.</span>
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {REASONS.map((r, i) => (
              <Reveal key={r.title} delay={i * 0.1}>
                <div className="milled group h-full rounded-3xl p-8">
                  <div className="mb-7 flex items-center justify-center w-[68px] h-[68px] rounded-full border border-white/10 bg-white/[0.03] backdrop-blur">
                    <img src={r.icon} alt="" aria-hidden="true" className="w-9 h-9 object-contain apm-icon-glow" />
                  </div>
                  <h3 className="font-display font-medium text-lg text-white mb-3 leading-snug tracking-tight">{r.title}</h3>
                  <p className="text-sm text-white/55 leading-relaxed">{r.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* POKAŻEMY CI JAK DZIAŁAĆ */}
      <section className="relative py-[clamp(80px,11vw,150px)] overflow-hidden">
        <div className="container-pm grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <Reveal>
            <Overline className="mb-6">Krok po kroku</Overline>
            <h2 className="font-display font-light text-white text-[clamp(30px,4.6vw,64px)] leading-[1.02] tracking-[-0.03em]">
              Pokażemy Ci,<br /><span className="font-serif-apm italic font-medium text-[#c4b3fb]">jak działać.</span>
            </h2>
            <ul className="mt-10 space-y-5">
              {CHECKLIST.map((c, i) => (
                <Reveal key={c} delay={i * 0.08}>
                  <li className="flex items-center gap-4">
                    <span className="flex items-center justify-center w-7 h-7 rounded-full border border-[#8b5cf6]/40 bg-[#8b5cf6]/10 text-[#a78bfa] shrink-0">
                      <Check size={14} strokeWidth={2.5} />
                    </span>
                    <span className="text-white/70 text-base">{c}</span>
                  </li>
                </Reveal>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.15}>
            <motion.div
              animate={{ y: [0, -16, 0] }}
              transition={{ duration: 6.5, repeat: Infinity, ease: "easeInOut" }}
              className="relative rounded-3xl overflow-hidden border border-white/10 shadow-[0_40px_100px_-30px_rgba(139,92,246,0.5)]"
            >
              <img src={IMG.workspace} alt="Analizy APM" className="w-full h-[460px] object-cover object-[72%_42%]" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#030206]/40 to-transparent" />
            </motion.div>
          </Reveal>
        </div>
      </section>

      {/* STATEMENT + MECHANISM */}
      <section className="relative py-[clamp(90px,13vw,190px)] border-y border-white/[0.06] overflow-hidden" style={{ background: "#0A0812" }}>
        <motion.img
          src="/apm/dekor-02-mechanizm.png"
          alt="" aria-hidden="true"
          animate={{ rotate: 360 }}
          transition={{ duration: 70, repeat: Infinity, ease: "linear" }}
          className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[150%] max-w-none opacity-[0.14]"
          style={{ filter: "drop-shadow(0 0 40px rgba(139,92,246,0.4))" }}
        />
        <AmbientGlow className="left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[560px] h-[560px] opacity-25" />
        <div className="container-pm relative max-w-[980px] mx-auto text-center">
          <Reveal>
            <h2 className="font-display font-light text-white text-[clamp(26px,3.8vw,50px)] leading-[1.16] tracking-[-0.02em]">
              Możesz latami uczyć się na własnych błędach albo skorzystać z{" "}
              <span className="font-serif-apm italic font-medium text-[#c4b3fb]">wiedzy ludzi, którzy już przeszli tę drogę.</span>
            </h2>
          </Reveal>
        </div>
      </section>

      {/* CO OTRZYMUJESZ — grid borders skeleton */}
      <section id="co-zyskujesz" className="relative py-[clamp(80px,11vw,150px)]">
        <div className="container-pm">
          <Reveal className="max-w-[720px] mb-16">
            <Overline className="mb-6">Członkostwo</Overline>
            <h2 className="font-display font-light text-[clamp(28px,4.4vw,56px)] leading-[1.04] tracking-[-0.025em]">
              Co otrzymujesz w <span className="font-serif-apm italic font-medium text-[#c4b3fb]">APM?</span>
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-white/10 rounded-2xl overflow-hidden">
            {BENEFITS.map((b, i) => {
              const Icon = b.icon;
              return (
                <Reveal key={b.title} delay={(i % 3) * 0.08}>
                  <div className="group relative h-full border-r border-b border-white/10 p-9 transition-colors duration-500 hover:bg-[#8b5cf6]/[0.05]">
                    <div className="mb-6 flex items-center justify-center w-11 h-11 rounded-xl border border-white/10 bg-white/[0.03] text-[#a78bfa] group-hover:border-[#8b5cf6]/40 transition-colors">
                      <Icon size={20} strokeWidth={1.7} />
                    </div>
                    <h3 className="font-display font-medium text-lg text-white mb-3 leading-snug tracking-tight">{b.title}</h3>
                    <p className="text-sm text-white/55 leading-relaxed">{b.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* JAK DOŁĄCZYĆ — zig-zag */}
      <section id="jak-dolaczyc" className="relative py-[clamp(80px,11vw,150px)] border-y border-white/[0.06] overflow-hidden" style={{ background: "#0A0812" }}>
        <div className="container-pm">
          <Reveal className="text-center max-w-[720px] mx-auto mb-20">
            <Overline className="mb-6">Proces</Overline>
            <h2 className="font-display font-light text-[clamp(28px,4.4vw,56px)] leading-[1.04] tracking-[-0.025em]">
              Jak <span className="font-serif-apm italic font-medium text-[#c4b3fb]">dołączyć?</span>
            </h2>
          </Reveal>
          <div className="relative">
            {/* central drawn line */}
            <motion.div
              initial={{ scaleY: 0 }} whileInView={{ scaleY: 1 }} viewport={{ once: true, margin: "-120px" }}
              transition={{ duration: 1.6, ease }}
              style={{ transformOrigin: "top" }}
              className="hidden md:block absolute left-1/2 top-8 bottom-8 -translate-x-1/2 w-px"
            >
              <div className="w-full h-full border-l border-dashed border-[#8b5cf6]/40" />
            </motion.div>

            <div className="space-y-14 md:space-y-24">
              {STEPS.map((s, i) => {
                const flip = i % 2 === 1;
                return (
                  <Reveal key={s.n} delay={0.05}>
                    <div className="grid md:grid-cols-2 gap-8 md:gap-16 items-center" data-testid={`apm-step-${s.n}`}>
                      <div className={`relative rounded-3xl overflow-hidden border border-white/10 ${flip ? "md:order-2" : ""}`}>
                        <div className="relative h-[260px] md:h-[300px] overflow-hidden">
                          <img src={s.img} alt={s.title} className="w-full h-full object-cover transition-transform duration-[900ms] hover:scale-105" />
                          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg,rgba(3,2,6,0.15) 0%,rgba(3,2,6,0.7) 100%)" }} />
                        </div>
                      </div>
                      <div className={`${flip ? "md:order-1 md:text-right md:pr-6" : "md:pl-6"}`}>
                        <span className="font-display font-light text-6xl md:text-7xl text-[#8b5cf6]/25 leading-none">{s.n}</span>
                        <h3 className="font-display font-medium text-2xl text-white mt-4 mb-3 tracking-tight">{s.title}</h3>
                        <p className={`text-white/55 leading-relaxed max-w-[38ch] ${flip ? "md:ml-auto" : ""}`}>{s.text}</p>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative py-[clamp(80px,11vw,150px)]">
        <div className="container-pm grid lg:grid-cols-[0.8fr_1.2fr] gap-12 lg:gap-20">
          <Reveal>
            <Overline className="mb-6">FAQ</Overline>
            <h2 className="font-display font-light text-[clamp(28px,4vw,52px)] leading-[1.04] tracking-[-0.025em]">
              Najczęstsze <span className="font-serif-apm italic font-medium text-[#c4b3fb]">pytania</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="w-full" data-testid="apm-faq">
              {FAQ.map((item, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-b border-white/10">
                  <AccordionTrigger className="text-left font-display font-medium text-lg sm:text-xl text-white hover:text-[#c4b3fb] hover:no-underline py-6 [&>svg]:text-[#a78bfa]">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-white/55 text-base leading-relaxed pb-6 max-w-[62ch]">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-[clamp(90px,13vw,180px)] overflow-hidden border-t border-white/[0.06]" style={{ background: "#0A0812" }}>
        <AmbientGlow className="left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[70%] h-[70%] opacity-30" />
        <div className="container-pm relative text-center max-w-[1100px] mx-auto">
          <Reveal>
            <h2 className="font-display font-light text-white text-[clamp(38px,8vw,120px)] leading-[0.98] tracking-[-0.035em]">
              Twoja historia<br />może zacząć się <span className="font-serif-apm italic font-medium text-[#c4b3fb]">dzisiaj.</span>
            </h2>
            <p className="mt-8 text-white/60 text-base sm:text-lg max-w-[52ch] mx-auto">
              Wejdź do APM, zdobądź wiedzę i zacznij budować wynik, z którego będziesz dumny.
            </p>
            <button
              onClick={() => setModal(true)}
              data-testid="apm-cta-join"
              className="btn-premium group mt-11 inline-flex items-center gap-3 h-[60px] px-10 rounded-full bg-[#8b5cf6] text-white text-[13px] uppercase tracking-[0.12em] font-semibold"
            >
              Dołącz do elitarnej grupy APM <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <p className="mt-5 text-sm text-white/45">
              Członkostwo już od <span className="text-[#c4b3fb] font-medium">149 zł/mies.</span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="relative py-16 border-t border-white/[0.06]" style={{ background: "#030206" }}>
        <div className="container-pm flex flex-col gap-8">
          <p className="text-white/35 text-xs sm:text-sm max-w-[70ch] leading-relaxed">
            APM przekazuje wiedzę i wsparcie. Wyniki zależą od Twoich decyzji, pracy i zaangażowania.
          </p>
          <div className="flex flex-col md:flex-row items-center justify-between gap-5 pt-6 border-t border-white/[0.06]">
            <span className="font-display font-medium text-lg tracking-[0.3em] text-white">APM</span>
            <div className="flex gap-8 text-[11px] uppercase tracking-[0.14em] text-white/40">
              <a href="/regulamin" className="hover:text-white/80 transition-colors">Regulamin</a>
              <a href="/polityka-prywatnosci" className="hover:text-white/80 transition-colors">Polityka prywatności</a>
              <a href="/kontakt" className="hover:text-white/80 transition-colors">Kontakt</a>
            </div>
            <span className="text-[11px] text-white/30">
              © {new Date().getFullYear()} APM · PMPOINT · Design &amp; copyright by{" "}
              <a href="https://apisdesign.pl" target="_blank" rel="noopener noreferrer" className="text-white/45 hover:text-[#a78bfa] transition-colors" data-testid="apm-credit">apisdesign.pl</a>
            </span>
          </div>
        </div>
      </footer>

      <ApmJoinModal open={modal} onClose={() => setModal(false)} />
    </div>
  );
}
